import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isTokenExpired } from '../../utils/auth';
import type { AuthResult } from '../../services/cognito';

export type AuthCredentialsState = {
  token: string | null;
  user: { organizationId?: string } | null;
};

/**
 * True when the error indicates auth failure we should refresh for:
 * - HTTP 401 from the API
 * - FETCH_ERROR while the Redux token is already expired (CORS-masked
 *   Cognito authorizer 401s omit Access-Control-Allow-Origin)
 */
export function shouldAttemptReauth(
  error: FetchBaseQueryError | undefined,
  token: string | null
): boolean {
  if (!error) return false;
  if (error.status === 401) return true;
  if (error.status === 'FETCH_ERROR') {
    return !token || isTokenExpired(token, 0);
  }
  return false;
}

export function needsProactiveRefresh(token: string | null): boolean {
  // Only refresh when we already hold a token that is expired / near expiry.
  // A null token must not trigger Cognito (unauthenticated routes / tests).
  return Boolean(token) && isTokenExpired(token!);
}

/**
 * Build setCredentials payload. REST tenant scope is the Redux / switcher org
 * (x-organization-id), not the JWT claim. Prefer the existing org whenever it
 * is set so a stale custom:organization-id (claim lag after org switch) cannot
 * revert the active organisation.
 */
export function credentialsFromRefresh(
  refreshResult: AuthResult,
  existingOrgId?: string
) {
  return {
    user: {
      id: refreshResult.user.userId,
      email: refreshResult.user.email,
      name: refreshResult.user.name,
      organizationId: existingOrgId || refreshResult.user.organizationId,
      groups: refreshResult.user.groups,
    },
    token: refreshResult.tokens.idToken,
    accessToken: refreshResult.tokens.accessToken,
    refreshToken: refreshResult.tokens.refreshToken,
  };
}
